import React, { FC, useState, useCallback } from "react";

import { useUpdateApplicationMutation, useGetAppInfoQuery, UpdateAppInput } from "generated/graphql";
import { wrapUrqlMutation } from "utils/graphql";
import { ClientApp } from "models/App";

import { AvailabilityStep, AvailabilityStepProps } from "../../components/setup/AvailabilityStep";

export interface AvailabilityStepContainerProps extends Omit<AvailabilityStepProps, "onNext" | "app"> {
    app?: Pick<ClientApp, "availability" | "appId" | "organizationId">;
    onNext(): void;
    onBack?(): void;
}

export const AvailabilityStepContainer: FC<AvailabilityStepContainerProps> = (props) => {
    const { app, onNext, onBack, ...restProps } = props;

    const [saving, setSaving] = useState(false);

    // Get the app data to pre-populate the form
    const [getAppInfo] = useGetAppInfoQuery({
        pause: !app?.appId,
        requestPolicy: "cache-and-network",
        variables: {
            appId: app?.appId || "",
        },
    });

    const { fetching: loading, stale: isStale } = getAppInfo;

    // Setup updateApplication mutation
    const updateApplication = wrapUrqlMutation(useUpdateApplicationMutation(), {
        onSuccessMessage: false,
    });

    const handleNext = useCallback(
        async (appData: Pick<ClientApp, "availability" | "appId" | "organizationId">) => {
            setSaving(true);

            try {
                // `appData.availability` already carries every existing availability field (it is
                // built upstream by spreading the app's current availability and only overriding
                // availableDays/defaultBusyDays - see AvailabilityStep#handleContinue). Sending it
                // through as-is - rather than hand-picking a subset - is required because `updateApp`
                // replaces the whole `availability` object on the server. Hand-picking fields (or
                // sending `{}` when availability is missing) silently wipes availabilityClasses,
                // defaultAvailabilityClass, maxTotalDailyAppointments, delayedJobTypes, and blockedDays.
                //
                // TODO: once the server-side deep-merging `updateAvailability` mutation is deployed and
                // regenerated via `yarn codegen:dev`, switch to it and send only the fields this step
                // owns (availableDays, defaultBusyDays) instead of round-tripping the whole object.
                if (appData?.appId && appData.availability) {
                    const updateAppInput: UpdateAppInput = {
                        availability: appData.availability,
                    };

                    await updateApplication({
                        appId: appData.appId,
                        app: updateAppInput,
                    });
                }

                // Success - proceed to next step
                onNext();
            } catch (err) {
                console.error("Failed to update availability:", err);
                // Still proceed to next step even if save fails - we don't want to block the user
                onNext();
            } finally {
                setSaving(false);
            }
        },
        [onNext, updateApplication]
    );

    return (
        <AvailabilityStep
            {...restProps}
            app={app}
            onNext={handleNext}
            onBack={onBack}
            saving={saving}
            loading={loading || isStale}
        />
    );
};
